import React, { useState } from 'react';

// === 決済方法マスタ（カテゴリ別） ===
export type PaymentMethodCategory = 'card' | 'qr' | 'wallet' | 'convenience';

type PaymentMethodMeta = {
  label: string;
  category: PaymentMethodCategory;
  bg: string;
  text: string;
};

const PAYMENT_METHODS: Record<string, PaymentMethodMeta> = {
  // カードブランド
  visa:       { label: 'Visa',       category: 'card',   bg: '#1A1F71', text: '#FFFFFF' },
  mastercard: { label: 'Mastercard', category: 'card',   bg: '#EB001B', text: '#FFFFFF' },
  jcb:        { label: 'JCB',        category: 'card',   bg: '#0E4C96', text: '#FFFFFF' },
  amex:       { label: 'AMEX',       category: 'card',   bg: '#006FCF', text: '#FFFFFF' },
  diners:     { label: 'Diners',     category: 'card',   bg: '#004B87', text: '#FFFFFF' },
  discover:   { label: 'Discover',   category: 'card',   bg: '#FF6000', text: '#FFFFFF' },
  // QR決済
  paypay:     { label: 'PayPay',     category: 'qr',     bg: '#FF0033', text: '#FFFFFF' },
  linepay:    { label: 'LINE Pay',   category: 'qr',     bg: '#00C300', text: '#FFFFFF' },
  merpay:     { label: 'メルペイ',    category: 'qr',     bg: '#4DC4FF', text: '#FFFFFF' },
  // ウォレット
  applepay:   { label: 'Apple Pay',  category: 'wallet', bg: '#000000', text: '#FFFFFF' },
  googlepay:  { label: 'Google Pay', category: 'wallet', bg: '#4285F4', text: '#FFFFFF' },
  konbini:    { label: 'コンビニ支払い', category: 'convenience', bg: '#166534', text: '#FFFFFF' },
};

export const CATEGORY_LABELS: Record<PaymentMethodCategory, string> = {
  card: 'クレジットカード',
  qr: 'QR決済',
  wallet: 'ウォレット',
  convenience: 'コンビニ支払い',
};

export const AVAILABLE_METHODS_BY_CATEGORY: Record<PaymentMethodCategory, string[]> = {
  card: ['visa', 'mastercard', 'jcb', 'amex', 'diners', 'discover'],
  qr: ['paypay', 'linepay', 'merpay'],
  wallet: ['applepay', 'googlepay'],
  convenience: ['konbini'],
};

export type PaymentMethodReviewGroup = {
  key: string;
  label: string;
  description: string;
  category: PaymentMethodCategory;
  methods: string[];
};

// 審査・店舗側ON/OFFの操作単位。永続化は既存互換のためブランド配列のまま扱う。
export const PAYMENT_METHOD_REVIEW_GROUPS: PaymentMethodReviewGroup[] = [
  {
    key: 'visa_mastercard',
    label: 'Visa / Master',
    description: 'Visa・Mastercardブランドのカード決済に対応します。',
    category: 'card',
    methods: ['visa', 'mastercard'],
  },
  {
    key: 'jcb_amex_diners_discover',
    label: 'JCB / AMEX / Diners / Discover',
    description: 'JCB・American Express・Diners Club・Discoverのカード決済に対応します。',
    category: 'card',
    methods: ['jcb', 'amex', 'diners', 'discover'],
  },
  {
    key: 'paypay',
    label: 'PayPay',
    description: 'PayPay残高から支払えます。',
    category: 'qr',
    methods: ['paypay'],
  },
  {
    key: 'applepay',
    label: 'Apple Pay',
    description: 'Apple端末から、登録済みカードで素早く支払えます。',
    category: 'wallet',
    methods: ['applepay'],
  },
  {
    key: 'googlepay',
    label: 'Google Pay',
    description: 'Android端末から、登録済みカードで支払えます。',
    category: 'wallet',
    methods: ['googlepay'],
  },
  {
    key: 'konbini',
    label: 'コンビニ支払い',
    description: '全国の主要コンビニで支払えます。',
    category: 'convenience',
    methods: ['konbini'],
  },
];

export const getPaymentMethodMeta = (method: string): PaymentMethodMeta | undefined => {
  return PAYMENT_METHODS[method.toLowerCase().replace(/[\s_-]+/g, '')];
};

// fincode が未対応のため UI に表示しない決済手段
export const UNSUPPORTED_METHODS = ['linepay', 'merpay'];

// 対応予定だが現在は準備中（選択不可で「準備中」表示）。
// Google Pay / Apple Pay は解禁済み（3軸: Admin available→Owner accepted で各サイト個別に有効化）。
// PayPay は fincode 契約・実装が完了したら解除する。
export const COMING_SOON_METHODS = ['paypay'];

// fincode が決済・払い出し時に店舗へ適用する手数料の表示値。
export const PAYMENT_METHOD_FEE_RATES: Record<string, string> = {
  visa: '3.6%', mastercard: '3.6%', jcb: '3.6%', amex: '3.6%', diners: '3.6%', discover: '3.6%',
  applepay: '3.6%', googlepay: '3.6%',
  konbini: '3.6%（最低150円/件）',
  paypay: '9.0%',
};

export const getPaymentMethodFeeRate = (method: string): string | undefined => {
  return PAYMENT_METHOD_FEE_RATES[method.toLowerCase().replace(/[\s_-]+/g, '')];
};

// 顧客が実際に選べる決済手段（Owner が有効化した accepted から、fincode 未対応・準備中を除外）。
// 顧客向けの決済手段表示/ゲートはこのリストを単一ソースにする。
// 準備中(paypay)は COMING_SOON_METHODS から外せば全画面で解禁される。
export const usableCustomerPaymentMethods = (accepted: string[] = []): string[] =>
  accepted.filter((m) => !UNSUPPORTED_METHODS.includes(m) && !COMING_SOON_METHODS.includes(m));

// === ロゴパス ===
const PAYMENT_LOGO_PATHS: Record<string, string> = {
  visa: '/images/payment/visa.svg',
  mastercard: '/images/payment/mastercard.svg',
  jcb: '/images/payment/jcb.svg',
  amex: '/images/payment/amex.svg',
  diners: '/images/payment/diners.svg',
  discover: '/images/payment/discover.svg',
  applepay: '/images/payment/applepay.svg',
  googlepay: '/images/payment/googlepay-mark.png',
};

const logoSizeClasses = {
  sm: 'h-5',
  md: 'h-6',
  lg: 'h-7',
};

const googlePayLogoSizeClasses = {
  sm: 'h-7',
  md: 'h-8',
  lg: 'h-9',
};

// === ロゴ付き決済方法表示 ===
const PaymentMethodLogo: React.FC<{ method: string; size: 'sm' | 'md' | 'lg' }> = ({ method, size }) => {
  const [imgError, setImgError] = useState(false);
  const key = method.toLowerCase().replace(/[\s_-]+/g, '');
  const logoPath = PAYMENT_LOGO_PATHS[key];
  const meta = PAYMENT_METHODS[key];
  const logoClassName = key === 'googlepay'
    ? `${googlePayLogoSizeClasses[size]} w-auto`
    : `${logoSizeClasses[size]} w-auto rounded`;

  if (!logoPath || imgError) {
    // fallback: テキストバッジ
    return <PaymentMethodBadge method={method} size={size} />;
  }

  return (
    <img
      src={logoPath}
      alt={meta?.label || method}
      title={meta?.label || method}
      className={logoClassName}
      onError={() => setImgError(true)}
    />
  );
};

// === 単一決済方法バッジ（テキスト版 - fallback & PaymentMethodList用） ===
interface PaymentMethodBadgeProps {
  method: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'h-6 px-1.5 text-[8px]',
  md: 'h-7 px-2 text-[9px]',
  lg: 'h-8 px-2.5 text-[10px]',
};

export const PaymentMethodBadge: React.FC<PaymentMethodBadgeProps> = ({ method, size = 'md', className = '' }) => {
  const meta = getPaymentMethodMeta(method);

  if (!meta) {
    return (
      <span className={`inline-flex items-center justify-center rounded border border-gray-200 bg-gray-100 font-bold text-gray-600 ${sizeClasses[size]} ${className}`}>
        {method}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded font-bold tracking-tight whitespace-nowrap ${sizeClasses[size]} ${className}`}
      style={{ backgroundColor: meta.bg, color: meta.text }}
      title={meta.label}
    >
      {meta.label}
    </span>
  );
};

// === 対応決済方法一覧（カテゴリ別レイアウト） ===
interface AcceptedPaymentMethodsProps {
  methods?: string[];
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AcceptedPaymentMethods: React.FC<AcceptedPaymentMethodsProps> = ({
  methods = ['visa', 'mastercard', 'jcb', 'amex'],
  size = 'sm',
  className = '',
}) => {
  if (!methods || methods.length === 0) return null;

  // カテゴリ別にグループ化
  const grouped: Partial<Record<PaymentMethodCategory, string[]>> = {};
  for (const m of methods) {
    const key = m.toLowerCase().replace(/[\s_-]+/g, '');
    const meta = PAYMENT_METHODS[key];
    if (meta) {
      if (!grouped[meta.category]) grouped[meta.category] = [];
      grouped[meta.category]!.push(m);
    }
  }

  const categories = (['card', 'qr', 'wallet', 'convenience'] as PaymentMethodCategory[]).filter(c => grouped[c]);

  return (
    <div className={`flex flex-wrap gap-x-6 gap-y-3 ${className}`}>
      {categories.map((cat) => (
        <div key={cat} className="flex flex-col">
          <span className="text-[10px] text-gray-400 mb-1">{CATEGORY_LABELS[cat]}</span>
          <div className="flex items-center gap-1.5">
            {grouped[cat]!.map((method) => (
              <PaymentMethodLogo key={method} method={method} size={size} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
